import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { uploadRecording } from '../services/recordingsService';
import {
  ChevronRight,
  Volume2,
  Mic,
  Square,
  Sparkles,
  RotateCcw,
  Timer,
  Send,
  CheckCircle2,
  Play,
  Target,
  MessageSquare,
  Theater,
} from 'lucide-react';

/* fr-FR speech (Coqui-style stand-in via browser SpeechSynthesis) */
const speak = (text, rate = 0.95) => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = 'fr-FR';
    u.rate = rate;
    window.speechSynthesis.speak(u);
  } catch { /* silent */ }
};

/* -----------------------------------------------------------
   20 A1 role-play missions
   Each = mission name + AI opening line + conversation guide + goal
----------------------------------------------------------- */
const MISSIONS_A1 = [
  {
    id: 'm1', emoji: '☕', title: 'Au café',
    mission: 'Commander une boisson et quelque chose à manger.',
    ai: 'Bonjour ! Bienvenue. Qu\'est-ce que vous désirez ?',
    guide: ['Choisis ta boisson.', 'Précise la taille.', 'Demande le prix.', 'Indique ton mode de paiement.'],
    goal: 'Obtenir ta commande.',
  },
  {
    id: 'm2', emoji: '🥖', title: 'À la boulangerie',
    mission: 'Acheter du pain et une pâtisserie.',
    ai: 'Bonjour ! Qu\'est-ce qu\'il vous faut aujourd\'hui ?',
    guide: ['Choisis tes produits.', 'Indique la quantité.', 'Demande le prix.'],
    goal: 'Faire un achat simple.',
  },
  {
    id: 'm3', emoji: '🧭', title: 'Demander son chemin',
    mission: 'Trouver la gare.',
    ai: 'Bonjour. Vous cherchez quelque chose ?',
    guide: ['Demande une direction.', 'Comprends les indications.', 'Remercie poliment.'],
    goal: 'Trouver ton chemin.',
  },
  {
    id: 'm4', emoji: '🚆', title: 'Acheter un billet de train',
    mission: 'Acheter un billet.',
    ai: 'Bonjour. Pour quelle destination ?',
    guide: ['Indique la destination.', 'Précise la date.', 'Précise l\'heure.', 'Demande le prix.'],
    goal: 'Acheter le billet.',
  },
  {
    id: 'm5', emoji: '🏨', title: 'À l\'hôtel',
    mission: 'Faire ton enregistrement.',
    ai: 'Bonjour. Avez-vous une réservation ?',
    guide: ['Donne ton nom.', 'Indique la durée du séjour.', 'Demande ton numéro de chambre.'],
    goal: 'Obtenir ta chambre.',
  },
  {
    id: 'm6', emoji: '👋', title: 'Se présenter',
    mission: 'Faire connaissance.',
    ai: 'Bonjour ! Comment vous appelez-vous ?',
    guide: ['Donne ton nom.', 'Donne ton âge.', 'Indique ta nationalité.', 'Parle de ta profession.', 'Évoque tes loisirs.'],
    goal: 'Te présenter simplement.',
  },
  {
    id: 'm7', emoji: '🥕', title: 'Au marché',
    mission: 'Acheter des fruits et légumes.',
    ai: 'Bonjour ! Que voulez-vous acheter ?',
    guide: ['Choisis tes produits.', 'Indique les quantités.', 'Demande les prix.'],
    goal: 'Faire tes courses.',
  },
  {
    id: 'm8', emoji: '🩺', title: 'Chez le médecin',
    mission: 'Expliquer un problème simple.',
    ai: 'Bonjour. Qu\'est-ce qui ne va pas ?',
    guide: ['Décris tes symptômes simples.', 'Précise la durée.', 'Demande des conseils.'],
    goal: 'Expliquer ton problème.',
  },
  {
    id: 'm9', emoji: '📞', title: 'Au téléphone',
    mission: 'Laisser un message simple.',
    ai: 'Bonjour, vous êtes bien chez Martin. Laissez un message après le bip.',
    guide: ['Présente-toi.', 'Explique l\'objet de ton appel.', 'Laisse ton numéro.', 'Termine poliment.'],
    goal: 'Te présenter et laisser un message.',
  },
  {
    id: 'm10', emoji: '🎉', title: 'Inviter un ami',
    mission: 'Inviter quelqu\'un à une activité.',
    ai: 'Bonjour ! Qu\'est-ce que tu fais ce week-end ?',
    guide: ['Fais une proposition.', 'Précise l\'heure.', 'Indique le lieu.', 'Demande confirmation.'],
    goal: 'Organiser une sortie.',
  },
  {
    id: 'm11', emoji: '🎬', title: 'Au cinéma',
    mission: 'Acheter une place.',
    ai: 'Bonjour. Pour quel film souhaitez-vous une place ?',
    guide: ['Précise le film.', 'Indique l\'horaire.', 'Précise le nombre de places.'],
    goal: 'Acheter ton billet.',
  },
  {
    id: 'm12', emoji: '📚', title: 'À la bibliothèque',
    mission: 'Chercher un livre.',
    ai: 'Bonjour. Puis-je vous aider ?',
    guide: ['Indique le type de livre.', 'Précise l\'auteur si possible.', 'Demande comment l\'emprunter.'],
    goal: 'Trouver un livre.',
  },
  {
    id: 'm13', emoji: '👕', title: 'Dans un magasin de vêtements',
    mission: 'Acheter un vêtement.',
    ai: 'Bonjour. Que cherchez-vous ?',
    guide: ['Indique la taille.', 'Précise la couleur.', 'Demande le prix.'],
    goal: 'Faire ton achat.',
  },
  {
    id: 'm14', emoji: '✈️', title: 'À l\'aéroport',
    mission: 'Faire l\'enregistrement.',
    ai: 'Bonjour. Puis-je voir votre passeport ?',
    guide: ['Indique ta destination.', 'Donne le nombre de bagages.', 'Demande ta porte d\'embarquement.'],
    goal: 'Préparer ton voyage.',
  },
  {
    id: 'm15', emoji: '🏡', title: 'Chez un nouveau voisin',
    mission: 'Faire connaissance.',
    ai: 'Bonjour ! Je viens d\'emménager ici.',
    guide: ['Présente-toi.', 'Parle du quartier.', 'Évoque tes activités.'],
    goal: 'Créer un premier contact.',
  },
  {
    id: 'm16', emoji: '🍽️', title: 'Au restaurant',
    mission: 'Commander un repas.',
    ai: 'Bonjour. Avez-vous choisi ?',
    guide: ['Choisis ton entrée.', 'Choisis ton plat.', 'Choisis ta boisson.', 'Choisis ton dessert.'],
    goal: 'Passer ta commande.',
  },
  {
    id: 'm17', emoji: '🏋️', title: 'Au club de sport',
    mission: 'S\'inscrire à une activité.',
    ai: 'Bonjour. Quel sport souhaitez-vous pratiquer ?',
    guide: ['Choisis ton sport.', 'Demande les horaires.', 'Demande le prix.'],
    goal: 'T\'inscrire.',
  },
  {
    id: 'm18', emoji: '🗺️', title: 'À l\'office de tourisme',
    mission: 'Demander des informations.',
    ai: 'Bonjour. Comment puis-je vous aider ?',
    guide: ['Demande quels monuments visiter.', 'Demande quelles activités sont disponibles.', 'Demande les horaires.'],
    goal: 'Préparer une visite.',
  },
  {
    id: 'm19', emoji: '🎂', title: 'Organiser un anniversaire',
    mission: 'Préparer une fête avec un ami.',
    ai: 'Alors, qu\'est-ce qu\'on organise pour l\'anniversaire ?',
    guide: ['Parle des invités.', 'Choisis la nourriture.', 'Propose des activités.'],
    goal: 'Préparer la fête.',
  },
  {
    id: 'm20', emoji: '🎓', title: 'Rencontrer un nouvel étudiant',
    mission: 'Faire connaissance.',
    ai: 'Bonjour ! C\'est mon premier jour ici.',
    guide: ['Parle de tes études.', 'Parle de la ville.', 'Évoque tes intérêts.'],
    goal: 'Accueillir la personne.',
  },
];

const MISSIONS_A2 = [
  {
    id: 'm1', emoji: '🏨', title: 'Réservation d\'hôtel perdue',
    mission: 'Votre réservation n\'apparaît pas dans le système.',
    ai: 'Bonjour. Je ne trouve malheureusement aucune réservation à votre nom.',
    goal: 'Explique la situation et trouve une solution.',
  },
  {
    id: 'm2', emoji: '🚆', title: 'Train annulé',
    mission: 'Votre train vient d\'être annulé.',
    ai: 'Nous sommes désolés. Votre train a été annulé.',
    goal: 'Demande des informations et trouve une alternative.',
  },
  {
    id: 'm3', emoji: '🍽️', title: 'Erreur au restaurant',
    mission: 'Le serveur vous apporte un mauvais plat.',
    ai: 'Voici votre commande. Bon appétit !',
    goal: 'Signale l\'erreur avec politesse.',
  },
  {
    id: 'm4', emoji: '👛', title: 'Portefeuille perdu',
    mission: 'Vous avez perdu votre portefeuille.',
    ai: 'Bonjour. Comment puis-je vous aider ?',
    goal: 'Décris la situation et demande de l\'aide.',
  },
  {
    id: 'm5', emoji: '🗺️', title: 'Organiser un week-end entre amis',
    mission: 'Préparer un week-end avec un ami.',
    ai: 'Alors, où voudrais-tu partir ce week-end ?',
    goal: 'Choisis une destination, des activités et un budget.',
  },
  {
    id: 'm6', emoji: '🎂', title: 'Préparer une fête d\'anniversaire',
    mission: 'Organiser une fête.',
    ai: 'Combien de personnes souhaites-tu inviter ?',
    goal: 'Planifie l\'événement.',
  },
  {
    id: 'm7', emoji: '🎓', title: 'Conseiller un ami qui apprend le français',
    mission: 'Donner des conseils pratiques.',
    ai: 'J\'aimerais progresser rapidement en français. Que me conseilles-tu ?',
    goal: 'Partage plusieurs recommandations.',
  },
  {
    id: 'm8', emoji: '🤝', title: 'Aider un ami stressé',
    mission: 'Donner des conseils.',
    ai: 'Je suis très stressé en ce moment et je ne sais plus quoi faire.',
    goal: 'Rassure et propose des solutions.',
  },
  {
    id: 'm9', emoji: '🏠', title: 'Chercher un appartement',
    mission: 'Poser des questions sur un logement.',
    ai: 'Bonjour. L\'appartement est toujours disponible.',
    goal: 'Obtiens toutes les informations nécessaires.',
  },
  {
    id: 'm10', emoji: '🎨', title: 'Inscription à une activité culturelle',
    mission: 'S\'inscrire à un cours ou un club.',
    ai: 'Bonjour. Souhaitez-vous des informations sur nos activités ?',
    goal: 'Pose des questions et choisis une activité.',
  },
  {
    id: 'm11', emoji: '🛍️', title: 'Réclamation dans un magasin',
    mission: 'Rapporter un produit défectueux.',
    ai: 'Bonjour. Quel est le problème avec votre achat ?',
    goal: 'Explique la situation et demande une solution.',
  },
  {
    id: 'm12', emoji: '📞', title: 'Appeler le service client',
    mission: 'Résoudre un problème lié à un service.',
    ai: 'Bonjour. Comment puis-je vous aider aujourd\'hui ?',
    goal: 'Décris clairement le problème.',
  },
  {
    id: 'm13', emoji: '🏘️', title: 'Discuter avec un voisin',
    mission: 'Résoudre un petit conflit.',
    ai: 'Bonjour. Y a-t-il un problème ?',
    goal: 'Parle calmement et trouve un compromis.',
  },
  {
    id: 'm14', emoji: '✈️', title: 'Organiser un voyage à l\'étranger',
    mission: 'Préparer un voyage.',
    ai: 'Avez-vous déjà choisi votre destination ?',
    goal: 'Discute du transport, du budget et des activités.',
  },
  {
    id: 'm15', emoji: '🩺', title: 'Chez le médecin',
    mission: 'Décrire un problème de santé.',
    ai: 'Bonjour. Pouvez-vous me décrire vos symptômes ?',
    goal: 'Explique ton état et réponds aux questions.',
  },
  {
    id: 'm16', emoji: '🧳', title: 'À l\'agence de voyages',
    mission: 'Comparer plusieurs offres.',
    ai: 'Nous avons plusieurs possibilités pour vos vacances.',
    goal: 'Pose des questions et choisis une offre.',
  },
  {
    id: 'm17', emoji: '🌍', title: 'Organiser un événement associatif',
    mission: 'Préparer une activité pour une association.',
    ai: 'Quel type d\'événement aimeriez-vous organiser ?',
    goal: 'Planifie les détails.',
  },
  {
    id: 'm18', emoji: '🧭', title: 'Aider un touriste perdu',
    mission: 'Donner des indications détaillées.',
    ai: 'Excusez-moi, pourriez-vous m\'aider ? Je cherche le centre-ville.',
    goal: 'Explique clairement le chemin.',
  },
  {
    id: 'm19', emoji: '👥', title: 'Rejoindre un club ou une association',
    mission: 'Se présenter et discuter de ses intérêts.',
    ai: 'Bienvenue ! Pouvez-vous vous présenter ?',
    goal: 'Participe à la conversation.',
  },
  {
    id: 'm20', emoji: '📋', title: 'Projet de classe ou de groupe',
    mission: 'Organiser un travail collectif.',
    ai: 'Nous devons répartir les tâches du projet.',
    goal: 'Discute des responsabilités et du calendrier.',
  },
];

const MISSIONS_B1 = [
  {
    id: 'm1', emoji: '🏛️', title: 'Conseil municipal des jeunes',
    mission: 'La ville dispose d\'un budget limité. Vous devez convaincre le conseil de financer votre projet.',
    ai: 'Nous devons choisir entre plusieurs projets pour améliorer la ville. Lequel soutenez-vous et pourquoi ?',
    goal: 'Défendre un projet et convaincre les autres.',
  },
  {
    id: 'm2', emoji: '🎪', title: 'Organiser un festival local',
    mission: 'Préparer un festival pour les habitants.',
    ai: 'Nous avons plusieurs propositions d\'activités. Comment devons-nous organiser l\'événement ?',
    goal: 'Présenter des idées et trouver un compromis.',
  },
  {
    id: 'm3', emoji: '♻️', title: 'Réduire les déchets dans l\'école',
    mission: 'Proposer des solutions concrètes.',
    ai: 'Beaucoup d\'élèves ne participent pas aux initiatives écologiques. Que pouvons-nous faire ?',
    goal: 'Convaincre et proposer des actions.',
  },
  {
    id: 'm4', emoji: '🤲', title: 'Choisir un projet associatif',
    mission: 'Votre association doit lancer un nouveau projet.',
    ai: 'Plusieurs membres ont des priorités différentes. Quelle direction devons-nous prendre ?',
    goal: 'Négocier et parvenir à un accord.',
  },
  {
    id: 'm5', emoji: '🏠', title: 'Colocation',
    mission: 'Des tensions apparaissent dans la colocation.',
    ai: 'Certains colocataires ne respectent pas les règles communes. Comment résoudre ce problème ?',
    goal: 'Trouver un compromis acceptable.',
  },
  {
    id: 'm6', emoji: '🌱', title: 'Consommation responsable',
    mission: 'Débattre d\'une campagne locale.',
    ai: 'Devons-nous encourager les habitants à acheter davantage de produits locaux ?',
    goal: 'Argumenter et défendre son opinion.',
  },
  {
    id: 'm7', emoji: '✈️', title: 'Voyage scolaire',
    mission: 'Organiser un voyage avec un budget limité.',
    ai: 'Nous devons choisir la destination et les activités. Quelles sont vos propositions ?',
    goal: 'Négocier et justifier ses choix.',
  },
  {
    id: 'm8', emoji: '🤝', title: 'Conflit dans une équipe',
    mission: 'Résoudre un désaccord entre collègues.',
    ai: 'Deux membres de l\'équipe ne sont pas d\'accord sur la direction du projet. Que proposez-vous ?',
    goal: 'Médiation et recherche de solutions.',
  },
  {
    id: 'm9', emoji: '🌳', title: 'Projet écologique de quartier',
    mission: 'Lancer une initiative environnementale.',
    ai: 'Comment convaincre davantage d\'habitants de participer ?',
    goal: 'Présenter une stratégie convaincante.',
  },
  {
    id: 'm10', emoji: '🏘️', title: 'Espaces verts ou logements ?',
    mission: 'Participer à une consultation citoyenne.',
    ai: 'Certains habitants souhaitent davantage de logements, d\'autres davantage d\'espaces verts. Quel est votre avis ?',
    goal: 'Développer une argumentation équilibrée.',
  },
  {
    id: 'm11', emoji: '🎗️', title: 'Organiser une collecte solidaire',
    mission: 'Préparer une action caritative.',
    ai: 'Comment pouvons-nous mobiliser davantage de participants ?',
    goal: 'Proposer un plan d\'action.',
  },
  {
    id: 'm12', emoji: '🎓', title: 'Création d\'un club étudiant',
    mission: 'Lancer une nouvelle activité.',
    ai: 'Quel type de club devrait être créé et pourquoi ?',
    goal: 'Convaincre les membres du comité.',
  },
  {
    id: 'm13', emoji: '🚇', title: 'Améliorer les transports locaux',
    mission: 'Participer à une réunion citoyenne.',
    ai: 'Les habitants se plaignent des transports actuels. Quelles améliorations suggérez-vous ?',
    goal: 'Argumenter et proposer des solutions.',
  },
  {
    id: 'm14', emoji: '🎨', title: 'Événement culturel de quartier',
    mission: 'Organiser un événement pour renforcer la vie locale.',
    ai: 'Quel type d\'événement attirerait le plus de participants ?',
    goal: 'Développer un projet réaliste.',
  },
  {
    id: 'm15', emoji: '💰', title: 'Partage du budget d\'une association',
    mission: 'Répartir des ressources limitées.',
    ai: 'Plusieurs projets demandent un financement. Comment répartir le budget ?',
    goal: 'Justifier ses priorités.',
  },
  {
    id: 'm16', emoji: '📣', title: 'Campagne de sensibilisation',
    mission: 'Créer une campagne citoyenne.',
    ai: 'Quel message devrait être mis en avant pour toucher le public ?',
    goal: 'Développer une stratégie persuasive.',
  },
  {
    id: 'm17', emoji: '🏪', title: 'Entreprise locale en difficulté',
    mission: 'Trouver des solutions pour soutenir un commerce local.',
    ai: 'Le commerce perd des clients depuis plusieurs mois. Que pourrait-il faire ?',
    goal: 'Analyser et proposer des actions.',
  },
  {
    id: 'm18', emoji: '🚲', title: 'Organisation d\'une journée sans voiture',
    mission: 'Préparer un événement écologique.',
    ai: 'Certains habitants sont favorables, d\'autres opposés. Comment les convaincre ?',
    goal: 'Répondre aux objections.',
  },
  {
    id: 'm19', emoji: '🙌', title: 'Projet de bénévolat',
    mission: 'Recruter davantage de bénévoles.',
    ai: 'Comment encourager les habitants à participer davantage ?',
    goal: 'Présenter des arguments convaincants.',
  },
  {
    id: 'm20', emoji: '🏙️', title: 'Améliorer la qualité de vie dans le quartier',
    mission: 'Proposer des améliorations concrètes.',
    ai: 'Si vous pouviez changer trois choses dans le quartier, lesquelles choisiriez-vous ?',
    goal: 'Développer des propositions réalistes et argumentées.',
  },
];

const MISSIONS_B2 = [
  {
    id: 'm1', emoji: '📊', title: 'Conseiller économique de la ville',
    mission: 'Proposer des mesures pour soutenir l\'économie locale.',
    ai: 'Les commerces locaux perdent des clients. Que devrait faire la municipalité ?',
    goal: 'Défendre tes propositions face aux questions de l\'AI et justifier chacune d\'elles.',
  },
  {
    id: 'm2', emoji: '📅', title: 'Débat sur la semaine de quatre jours',
    mission: 'Défendre ou critiquer cette réforme.',
    ai: 'Une semaine de quatre jours améliorerait-elle réellement la productivité ?',
    goal: 'Présenter ta position et répondre aux contre-arguments de l\'AI.',
  },
  {
    id: 'm3', emoji: '🏛️', title: 'Maire et habitants',
    mission: 'Répondre aux critiques concernant un projet urbain.',
    ai: 'De nombreux habitants s\'opposent à votre projet. Comment les convaincre ?',
    goal: 'Reformuler ton projet, répondre aux objections et proposer des compromis.',
  },
  {
    id: 'm4', emoji: '🏢', title: 'Réunion de direction',
    mission: 'Débattre d\'une stratégie pour une entreprise.',
    ai: 'Devons-nous investir dans de nouveaux marchés ou consolider nos activités actuelles ?',
    goal: 'Présenter une stratégie claire et anticiper les risques.',
  },
  {
    id: 'm5', emoji: '🏖️', title: 'Tourisme et économie locale',
    mission: 'Trouver un équilibre entre développement économique et qualité de vie.',
    ai: 'Le tourisme apporte des revenus, mais crée aussi des nuisances. Comment gérer cette situation ?',
    goal: 'Proposer un modèle équilibré et nuancé.',
  },
  {
    id: 'm6', emoji: '🏘️', title: 'Politique de logement',
    mission: 'Défendre une proposition de réforme.',
    ai: 'Comment rendre le logement plus accessible sans décourager les investisseurs ?',
    goal: 'Présenter une réforme cohérente et anticiper ses effets.',
  },
  {
    id: 'm7', emoji: '⚡', title: 'Transition énergétique',
    mission: 'Participer à une commission d\'experts.',
    ai: 'Quelles priorités devraient être fixées pour réussir la transition énergétique ?',
    goal: 'Hiérarchiser tes priorités et défendre leur faisabilité.',
  },
  {
    id: 'm8', emoji: '🤝', title: 'Négociation entre employeur et salariés',
    mission: 'Les salariés demandent davantage de flexibilité. Les dirigeants craignent une baisse de performance.',
    ai: 'Les salariés demandent davantage de flexibilité. Les dirigeants craignent une baisse de performance.',
    goal: 'Négocier une solution équilibrée.',
  },
  {
    id: 'm9', emoji: '🌱', title: 'Débat sur la consommation responsable',
    mission: 'Défendre votre position.',
    ai: 'Les consommateurs sont-ils réellement prêts à modifier leurs habitudes ?',
    goal: 'Défendre ta position avec exemples concrets et nuancer.',
  },
  {
    id: 'm10', emoji: '🏙️', title: 'Conférence sur l\'avenir des villes',
    mission: 'Présenter une vision et répondre aux questions.',
    ai: 'À quoi devrait ressembler une ville durable dans vingt ans ?',
    goal: 'Présenter une vision prospective et la défendre.',
  },
  {
    id: 'm11', emoji: '🗳️', title: 'Consultation citoyenne',
    mission: 'Réagir à des propositions contradictoires.',
    ai: 'Les ressources publiques sont limitées. Quelles priorités choisir ?',
    goal: 'Trancher entre des priorités contradictoires et justifier tes choix.',
  },
  {
    id: 'm12', emoji: '🚨', title: 'Directeur d\'une entreprise en crise',
    mission: 'Répondre aux préoccupations des employés.',
    ai: 'Comment comptez-vous restaurer la confiance au sein de l\'entreprise ?',
    goal: 'Rassurer, expliquer un plan d\'action et tenir le débat.',
  },
  {
    id: 'm13', emoji: '🌍', title: 'Table ronde sur le commerce international',
    mission: 'Participer à un débat économique.',
    ai: 'Les échanges internationaux profitent-ils à tous les pays de la même manière ?',
    goal: 'Analyser les bénéfices et les déséquilibres avec nuance.',
  },
  {
    id: 'm14', emoji: '💻', title: 'Débat sur le télétravail',
    mission: 'Défendre votre point de vue.',
    ai: 'Le télétravail améliore-t-il réellement la qualité de vie ?',
    goal: 'Défendre ta position face à des contre-arguments solides.',
  },
  {
    id: 'm15', emoji: '🎯', title: 'Entretien pour un poste à responsabilité',
    mission: 'Convaincre un recruteur.',
    ai: 'Pourquoi êtes-vous la meilleure personne pour ce poste ?',
    goal: 'Mettre en valeur tes compétences et répondre aux questions exigeantes.',
  },
  {
    id: 'm16', emoji: '🆘', title: 'Réunion de gestion de crise',
    mission: 'Prendre des décisions sous pression.',
    ai: 'Une crise importante menace l\'organisation. Quelles mesures prenez-vous ?',
    goal: 'Décider rapidement, hiérarchiser et expliquer ta stratégie.',
  },
  {
    id: 'm17', emoji: '🚦', title: 'Débat sur les transports urbains',
    mission: 'Trouver un équilibre entre mobilité et environnement.',
    ai: 'Les restrictions de circulation sont-elles justifiées ?',
    goal: 'Argumenter pour ou contre, en intégrant les usagers concernés.',
  },
  {
    id: 'm18', emoji: '💰', title: 'Projet d\'investissement public',
    mission: 'Défendre l\'utilisation d\'un budget important.',
    ai: 'Pourquoi ce projet mérite-t-il d\'être financé plutôt qu\'un autre ?',
    goal: 'Construire une justification stratégique et chiffrée.',
  },
  {
    id: 'm19', emoji: '⚖️', title: 'Médiation entre groupes aux intérêts opposés',
    mission: 'Trouver un compromis.',
    ai: 'Les deux parties refusent actuellement toute concession. Comment avancer ?',
    goal: 'Faire émerger un compromis et reformuler les positions.',
  },
  {
    id: 'm20', emoji: '🌐', title: 'Grand débat citoyen',
    mission: 'Développer une argumentation complète.',
    ai: 'Quel est aujourd\'hui le défi le plus important pour notre société et comment faudrait-il y répondre ?',
    goal: 'Choisir un enjeu, le défendre, répondre aux objections et nuancer.',
  },
];

const MISSIONS_BY_LEVEL = { A1: MISSIONS_A1, A2: MISSIONS_A2, B1: MISSIONS_B1, B2: MISSIONS_B2 };
const ENABLED_LEVELS = ['A1', 'A2', 'B1', 'B2'];
const ALL_LEVELS = ['A1', 'A2', 'B1', 'B2'];

const TIMER_MODES_BY_LEVEL = {
  A1: [
    { id: 'easy', name: 'Découverte', seconds: 30, sub: '30 sec' },
    { id: 'standard', name: 'Standard', seconds: 60, sub: '60 sec' },
    { id: 'challenge', name: 'Défi', seconds: 90, sub: '90 sec' },
  ],
  A2: [
    { id: 'standard', name: 'Standard', seconds: 60, sub: '60 sec' },
    { id: 'challenge', name: 'Challenge', seconds: 90, sub: '90 sec' },
    { id: 'expert', name: 'Expert', seconds: 120, sub: '2 min' },
  ],
  B1: [
    { id: 'standard', name: 'Standard', seconds: 120, sub: '2 min' },
    { id: 'challenge', name: 'Challenge', seconds: 180, sub: '3 min' },
    { id: 'expert', name: 'Expert', seconds: 240, sub: '4 min' },
  ],
  B2: [
    { id: 'standard', name: 'Standard', seconds: 180, sub: '3 min' },
    { id: 'challenge', name: 'Challenge', seconds: 300, sub: '5 min' },
    { id: 'expert', name: 'Expert', seconds: 360, sub: '6 min' },
  ],
};

const DEFAULT_TIMER_ID_BY_LEVEL = { A1: 'challenge', A2: 'challenge', B1: 'challenge', B2: 'challenge' };

const FEEDBACK_METRICS_BY_LEVEL = {
  A1: ['Prononciation', 'Fluidité', 'Compréhension', 'Réactivité', 'Vocabulaire utilisé', 'Réussite de la mission'],
  A2: ['Prononciation', 'Fluidité', 'Compréhension de la situation', 'Réactivité', 'Utilisation du vocabulaire', 'Capacité à résoudre le problème', 'Pertinence des réponses'],
  B1: ['Prononciation', 'Fluidité', 'Richesse lexicale', 'Cohérence des arguments', 'Utilisation des connecteurs', 'Capacité à convaincre', 'Gestion du dialogue', 'Utilisation des structures complexes'],
  B2: ['Prononciation', 'Fluidité', 'Richesse lexicale', 'Cohérence argumentative', 'Capacité de persuasion', 'Réponse aux objections', 'Utilisation des connecteurs complexes', 'Gestion du débat', 'Nuance des arguments'],
};

const TEACHER_MESSAGE_BY_LEVEL = {
  A1: {
    intro: 'Votre jeu de rôle a été enregistré. Votre professeur analysera :',
    bullets: [
      'votre capacité à communiquer',
      'votre prononciation',
      'votre compréhension de la situation',
      'votre fluidité',
      'votre utilisation du vocabulaire',
    ],
    closing: 'Les commentaires détaillés seront disponibles dans votre tableau de bord.',
  },
  A2: {
    intro: 'Votre jeu de rôle a été enregistré. Votre professeur analysera :',
    bullets: [
      'votre capacité à communiquer efficacement',
      'votre compréhension de la situation',
      'votre prononciation',
      'votre fluidité',
      'votre vocabulaire',
      'votre capacité à résoudre le problème proposé',
    ],
    closing: 'Les commentaires détaillés seront disponibles dans votre tableau de bord.',
  },
  B1: {
    intro: 'Votre jeu de rôle a été enregistré. Votre professeur analysera :',
    bullets: [
      'la qualité de votre argumentation',
      'votre capacité à convaincre',
      'votre interaction avec votre interlocuteur',
      'votre fluidité',
      'votre vocabulaire',
      'votre utilisation des structures avancées',
    ],
    closing: 'Les commentaires détaillés seront disponibles dans votre tableau de bord.',
  },
  B2: {
    intro: 'Votre débat a été enregistré. Votre professeur analysera :',
    bullets: [
      'la qualité de votre argumentation',
      'votre capacité à défendre une position',
      'votre gestion des objections',
      'votre aisance à l\'oral',
      'votre vocabulaire avancé',
      'votre capacité à nuancer vos propos',
    ],
    closing: 'Les commentaires détaillés seront disponibles dans votre tableau de bord.',
  },
};

const OBJECTIVE_BY_LEVEL = {
  A1: {
    title: 'Objectif A1',
    intro: 'À la fin du niveau A1, l\'apprenant doit être capable de :',
    items: [
      'se présenter',
      'acheter quelque chose',
      'demander des informations',
      'comprendre des questions simples',
      'répondre dans des situations quotidiennes',
      'maintenir une courte conversation réelle en français',
    ],
    closing: 'Le jeu de rôle est le pont entre la grammaire apprise et la conversation réelle.',
  },
  A2: {
    title: 'Objectif A2',
    intro: 'À la fin du niveau A2, l\'apprenant doit être capable de :',
    items: [
      'gérer une situation imprévue',
      'demander et fournir des informations',
      'résoudre un problème simple',
      'organiser une activité',
      'donner des conseils',
      'participer à une conversation de 1 à 2 minutes',
    ],
    closing: 'Dans des situations réalistes de la vie quotidienne — sans plan, sans script imposé.',
  },
  B1: {
    title: 'Objectif B1',
    intro: 'À la fin du niveau B1, l\'apprenant doit être capable de :',
    items: [
      'défendre une opinion',
      'convaincre un interlocuteur',
      'négocier et trouver des compromis',
      'collaborer sur un projet',
      'participer à une discussion structurée',
      'développer une argumentation cohérente',
    ],
    closing: 'Dans des situations réelles de la vie sociale, citoyenne et professionnelle.',
  },
  B2: {
    title: 'Objectif B2',
    intro: 'À la fin du niveau B2, l\'apprenant doit être capable de :',
    items: [
      'participer activement à un débat',
      'défendre une position complexe',
      'répondre à des contre-arguments',
      'convaincre un interlocuteur',
      'analyser des enjeux économiques, sociaux et professionnels',
      'présenter et justifier des solutions',
    ],
    closing: 'Avec aisance, précision et nuance dans des situations proches du monde académique, citoyen et professionnel.',
  },
};

const SUBTITLE_BY_LEVEL = {
  A1: '20 situations réelles A1 — café, marché, gare, hôtel, médecin, téléphone… L\'AI joue un personnage, à toi de répondre naturellement.',
  A2: '20 situations réelles A2 — gérer un imprévu, demander/fournir des infos, résoudre un problème, organiser, conseiller. 1 à 2 minutes de dialogue.',
  B1: '20 situations réelles B1 — défendre, convaincre, négocier, collaborer, argumenter. 2 à 4 minutes de discussion structurée avec un interlocuteur AI.',
  B2: '20 situations réelles B2 — débattre, défendre une position complexe, répondre aux objections, nuancer. 3 à 6 minutes de débat exigeant avec un interlocuteur AI.',
};

/* Simulated feedback dashboard */
function makeFeedback(level) {
  const labels = FEEDBACK_METRICS_BY_LEVEL[level] || FEEDBACK_METRICS_BY_LEVEL.A1;
  const base = 70 + Math.floor(Math.random() * 22);
  const scores = labels.map((label) => ({
    label,
    value: Math.min(98, Math.max(60, base + (Math.floor(Math.random() * 12) - 6))),
  }));
  return { scores };
}

/* Recorder hook */
function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const mrRef = useRef(null);
  const chunksRef = useRef([]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mrRef.current = mr;
      mr.start();
      setRecording(true);
      return true;
    } catch {
      alert('Microphone non disponible — vérifie les permissions de ton navigateur.');
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    if (mrRef.current && recording) {
      mrRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const reset = useCallback(() => {
    setAudioUrl(null);
    setAudioBlob(null);
    chunksRef.current = [];
  }, []);

  return { recording, audioUrl, audioBlob, start, stop, reset };
}

/* -----------------------------------------------------------
   Mission detail — AI opening / guide / record / feedback / submit
----------------------------------------------------------- */
function MissionDetail({ mission, timerSeconds, level = 'A1' }) {
  const { recording, audioUrl, audioBlob, start, stop, reset } = useRecorder();
  const [remaining, setRemaining] = useState(timerSeconds);
  const [phase, setPhase] = useState('idle');
  const [feedback, setFeedback] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const tickRef = useRef(null);

  useEffect(() => {
    setRemaining(timerSeconds);
    setPhase('idle');
    setFeedback(null);
    setSubmitted(false);
    setSubmitError('');
    reset();
    if (tickRef.current) clearInterval(tickRef.current);
    if (recording) stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission.id, timerSeconds]);

  useEffect(() => () => { if (tickRef.current) clearInterval(tickRef.current); }, []);

  const handleStart = async () => {
    setFeedback(null);
    reset();
    setRemaining(timerSeconds);
    const ok = await start();
    if (!ok) return;
    setPhase('recording');
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tickRef.current);
          stop();
          setPhase('done');
          setFeedback(makeFeedback(level));
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const handleStopEarly = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    stop();
    setPhase('done');
    setFeedback(makeFeedback(level));
  };

  const handleReset = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    setRemaining(timerSeconds);
    setPhase('idle');
    setFeedback(null);
    setSubmitted(false);
    setSubmitError('');
    reset();
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      setSubmitError("Aucun enregistrement à envoyer.");
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      await uploadRecording(audioBlob, {
        level,
        exerciseId: mission.id || `${level}-role`.toLowerCase(),
        exerciseTitle: `${mission.title || 'Jeu de rôle'} — Jeu de rôle`,
        promptId: String(mission.id || ''),
        promptText: mission.goal || mission.title || '',
        durationSeconds: timerSeconds - remaining,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Envoi impossible. Réessaie dans un instant.');
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((timerSeconds - remaining) / timerSeconds) * 100;
  const teacher = TEACHER_MESSAGE_BY_LEVEL[level];

  return (
    <div className="space-y-3" data-testid={`mission-detail-${mission.id}`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1A2F4A] to-[#243a58] rounded-2xl px-6 py-5 text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1.5">Jeu de rôle · {level}</p>
            <h2
              className="text-2xl font-bold flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="mission-title"
            >
              <span className="text-3xl" aria-hidden="true">{mission.emoji}</span>
              {mission.title}
            </h2>
            <p className="text-[11px] text-white/70 italic mt-1" data-testid="mission-tagline">
              Mission : {mission.mission}
            </p>
          </div>
        </div>
      </div>

      {/* AI opening line */}
      <div className="bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4" data-testid="ai-opening">
        <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1.5 flex items-center gap-1.5">
          <Theater className="w-3.5 h-3.5" /> L'AI commence la conversation
        </p>
        <p className="text-[15px] text-[#1A2F4A] font-medium italic leading-relaxed" data-testid="ai-line">
          <span className="text-[#D4AF37] mr-1">«</span>{mission.ai}<span className="text-[#D4AF37] ml-1">»</span>
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => speak(mission.ai)}
            className="inline-flex items-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-[#D4AF37] rounded-full px-4 py-2 text-xs font-bold transition-colors"
            data-testid="ai-listen"
          >
            <Volume2 className="w-3.5 h-3.5" /> Écouter l'AI
          </button>
          <span className="text-[11px] text-[#6B4E37] italic">Voix Coqui · fr-FR</span>
        </div>
      </div>

      {/* Conversation guide (optional — A1 has it, A2 doesn't) */}
      {mission.guide && mission.guide.length > 0 && (
        <div className="bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4" data-testid="conversation-guide">
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-2 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Conversation — points à aborder
          </p>
          <ul className="space-y-1.5 list-none">
            {mission.guide.map((g, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] text-[#1A2F4A] font-medium" data-testid={`guide-${i}`}>
                <span className="w-5 h-5 rounded-full bg-[#FAF7F0] border border-[#D4AF37]/30 text-[10px] font-bold text-[#1A2F4A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span>{g}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Goal */}
      <div className="bg-[#FAF7F0]/60 border border-[#D4AF37]/30 rounded-2xl px-5 py-3" data-testid="mission-goal">
        <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-1 flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5" /> Objectif de la mission
        </p>
        <p className="text-[13px] text-[#1A2F4A] font-bold">{mission.goal}</p>
      </div>

      {/* Record card */}
      <div className="bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4 space-y-3" data-testid="record-card">
        <div className="flex items-center justify-between text-xs">
          <div className="inline-flex items-center gap-1.5 text-[#6B4E37]">
            <Timer className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span>{phase === 'recording' ? `${remaining}s restantes` : `${timerSeconds}s pour répondre`}</span>
          </div>
          {phase === 'recording' && <span className="text-[#D4AF37] font-bold animate-pulse">● REC</span>}
        </div>
        <div className="w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden" data-testid="timer-bar">
          <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${progress}%` }} />
        </div>

        {phase === 'idle' && (
          <button
            onClick={handleStart}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full py-3 text-sm font-bold transition-colors"
            data-testid="record-start"
          >
            <Mic className="w-4 h-4" /> Démarrer la conversation
          </button>
        )}
        {phase === 'recording' && (
          <button
            onClick={handleStopEarly}
            className="w-full inline-flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white rounded-full py-3 text-sm font-bold animate-pulse"
            data-testid="record-stop"
          >
            <Square className="w-4 h-4" /> Arrêter
          </button>
        )}
        {phase === 'done' && (
          <button
            onClick={handleReset}
            className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-[#1A2F4A] text-[#1A2F4A] hover:bg-[#FAF7F0] rounded-full py-3 text-sm font-bold transition-colors"
            data-testid="record-retry"
          >
            <RotateCcw className="w-4 h-4" /> Réessayer la mission
          </button>
        )}

        {audioUrl && <audio controls src={audioUrl} className="w-full h-9" data-testid="playback" />}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className="space-y-3" data-testid="feedback-panel">
          <div className="bg-[#1A2F4A] rounded-2xl p-4 text-white">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider font-bold mb-3 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Analyse automatique
            </p>
            <div className="grid grid-cols-2 gap-3">
              {feedback.scores.map((m) => (
                <div key={m.label} data-testid={`metric-${m.label}`}>
                  <div className="flex justify-between text-[10px] text-white/70 mb-1 font-medium">
                    <span>{m.label}</span>
                    <span className="text-[#D4AF37] font-bold">{m.value}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${m.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 text-[11px] text-white/70 italic">
              Temps de parole : {timerSeconds - remaining}s / {timerSeconds}s · Mission : {mission.goal.toLowerCase()}
            </div>
          </div>

          {!submitted ? (
            <>
              <button
                onClick={handleSubmit}
                disabled={submitting || !audioBlob}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#1A2F4A] text-[#1A2F4A] hover:text-[#D4AF37] rounded-full py-2.5 text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-to-teacher"
              >
                <Send className="w-3.5 h-3.5" />
                {submitting ? 'Envoi en cours…' : 'Envoyer à mon professeur'}
              </button>
              {submitError && (
                <p className="text-[11px] text-red-600 mt-1.5" data-testid="submit-error">{submitError}</p>
              )}
            </>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="teacher-confirmation">
              <p className="text-[11px] text-emerald-700 uppercase tracking-wider font-bold mb-1.5">📋 Jeu de rôle enregistré</p>
              <p className="text-[12px] text-[#1A2F4A] leading-relaxed" data-testid="teacher-intro">
                {teacher.intro}
              </p>
              <ul className="space-y-1 list-none mt-2">
                {teacher.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12px] text-[#1A2F4A]" data-testid={`teacher-bullet-${i}`}>
                    <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">•</span>
                    {b}
                  </li>
                ))}
              </ul>
              <p className="text-[12px] text-[#6B4E37] italic mt-2" data-testid="teacher-closing">
                {teacher.closing}
              </p>
            </div>
          )}
        </div>
      )}

      {!feedback && (
        <div className="bg-[#FAF7F0]/60 border border-[#E8E4DC] rounded-2xl px-5 py-3" data-testid="feedback-placeholder">
          <p className="text-[11px] text-[#6B4E37] italic flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
            Termine ta mission pour recevoir une analyse de ta prononciation, ta fluidité et ta réactivité.
          </p>
        </div>
      )}
    </div>
  );
}

/* ===========================================================
   MAIN PAGE
=========================================================== */
export default function JeuxDeRole() {
  const [level, setLevel] = useState('A1');
  const missions = MISSIONS_BY_LEVEL[level];
  const timerModes = TIMER_MODES_BY_LEVEL[level];

  const [missionId, setMissionId] = useState(missions[0].id);
  const [mode, setMode] = useState(DEFAULT_TIMER_ID_BY_LEVEL[level]);

  useEffect(() => {
    const next = MISSIONS_BY_LEVEL[level];
    setMissionId(next[0].id);
    setMode(DEFAULT_TIMER_ID_BY_LEVEL[level]);
  }, [level]);

  const mission = missions.find((m) => m.id === missionId) || missions[0];
  const timerSeconds = (timerModes.find((t) => t.id === mode) || timerModes[1]).seconds;
  const objective = OBJECTIVE_BY_LEVEL[level];

  const goToMission = (id) => {
    setMissionId(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-[#FAF7F0] min-h-screen">
      {/* Header */}
      <section className="pt-12 pb-6">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center gap-1.5 text-[10px] text-[#999] mb-6 flex-wrap" data-testid="breadcrumb">
            <Link to="/speaking-lab" className="hover:text-[#D4AF37] transition-colors">Speaking Lab</Link>
            <ChevronRight className="w-3 h-3" />
            <span>S'exprimer</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#1A2F4A] font-bold">Jeux de rôle (situations réelles)</span>
          </div>

          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-10 bg-[#D4AF37]/50" />
              <Theater className="w-5 h-5 text-[#D4AF37]" />
              <div className="h-px w-10 bg-[#D4AF37]/50" />
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold text-[#1A2F4A] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="page-title"
            >
              Jeux de rôle — situations réelles
            </h1>
            <p className="text-sm text-[#6B4E37] italic max-w-md mx-auto leading-relaxed" data-testid="page-subtitle">
              {SUBTITLE_BY_LEVEL[level]}
            </p>

            {/* Level tabs */}
            <div className="flex justify-center mt-4">
              <div className="flex" data-testid="level-tabs">
                {ALL_LEVELS.map((lv, idx) => {
                  const isEnabled = ENABLED_LEVELS.includes(lv);
                  const isActive = level === lv;
                  const isFirst = idx === 0;
                  const isLast = idx === ALL_LEVELS.length - 1;
                  return (
                    <button
                      key={lv}
                      disabled={!isEnabled}
                      onClick={() => { if (isEnabled) setLevel(lv); }}
                      title={isEnabled ? '' : 'À venir'}
                      className={`text-[10px] font-bold tracking-wider px-3 py-1 transition-colors border ${
                        isActive
                          ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                          : isEnabled
                          ? 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                          : 'bg-white text-[#999] border-[#E8E4DC] cursor-not-allowed opacity-60'
                      } ${isFirst ? 'rounded-l-full' : ''} ${isLast ? 'rounded-r-full' : ''} ${!isFirst ? '-ml-px' : ''}`}
                      data-testid={`level-${lv}`}
                    >
                      {lv}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timer mode */}
      <section className="pb-2">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="bg-white border border-[#E8E4DC] rounded-2xl px-4 py-3" data-testid="timer-controls">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.2em] font-bold mb-1.5">Mode du chrono</p>
            <div className="grid grid-cols-3 gap-1.5" data-testid="timer-modes">
              {timerModes.map((t) => {
                const isActive = mode === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setMode(t.id)}
                    className={`rounded-full text-[11px] font-bold py-1.5 border transition-colors ${
                      isActive
                        ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                        : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37]'
                    }`}
                    data-testid={`timer-mode-${t.id}`}
                  >
                    {t.name} <span className="opacity-70 font-medium">· {t.sub}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Active mission detail */}
      <section className="pb-8 pt-4">
        <div className="max-w-[760px] mx-auto px-6">
          <MissionDetail key={level} mission={mission} timerSeconds={timerSeconds} level={level} />
        </div>
      </section>

      {/* Mission grid */}
      <section className="pb-20">
        <div className="max-w-[760px] mx-auto px-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] text-[#6B4E37] uppercase tracking-[0.25em] font-bold">
              Toutes les missions {level}
            </p>
            <p className="text-[10px] text-[#999]">{missions.length} missions</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="mission-grid">
            {missions.map((m, idx) => {
              const isActive = m.id === missionId;
              return (
                <button
                  key={m.id}
                  onClick={() => goToMission(m.id)}
                  className={`group rounded-xl px-3 py-3 text-left transition-all border ${
                    isActive
                      ? 'bg-[#1A2F4A] text-white border-[#1A2F4A]'
                      : 'bg-white text-[#1A2F4A] border-[#E8E4DC] hover:border-[#D4AF37] hover:shadow-sm'
                  }`}
                  data-testid={`mission-tile-${m.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-xl flex-shrink-0" aria-hidden="true">{m.emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className={`text-[9px] uppercase tracking-wider font-bold ${isActive ? 'text-[#D4AF37]' : 'text-[#999]'}`}>
                        Mission {idx + 1}
                      </p>
                      <p className="text-[11px] font-medium leading-tight mt-0.5 truncate">{m.title}</p>
                    </div>
                    {isActive && <Play className="w-3 h-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Objective note */}
          <div className="mt-6 bg-white border border-[#E8E4DC] rounded-2xl px-5 py-4" data-testid="objective-note">
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-bold mb-2" data-testid="objective-title">{objective.title}</p>
            <p className="text-[12px] text-[#1A2F4A]/90 italic mb-2">{objective.intro}</p>
            <ul className="space-y-1.5 text-[12px] text-[#1A2F4A]/90 leading-relaxed">
              {objective.items.map((g, i) => (
                <li key={i} className="flex items-start gap-2" data-testid={`objective-row-${i}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-[#6B4E37] italic mt-3 pt-3 border-t border-[#F3F4F6]" data-testid="objective-closing">
              {objective.closing}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
